import React, { useState } from "react";
import { CheckCircle2, XCircle, Clock, Circle, ShieldCheck, ShieldAlert, Globe, ChevronDown } from "lucide-react";
import { Pill } from "@admin/components/Primitives";

const ICON = {
  met: <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />,
  rejected: <XCircle size={16} className="text-rose-600 mt-0.5 shrink-0" />,
  pending: <Clock size={16} className="text-amber-500 mt-0.5 shrink-0" />,
  todo: <Circle size={16} className="text-slate-300 mt-0.5 shrink-0" />,
};
const STATUS_LABEL = { met: "Met", rejected: "Rejected", pending: "Awaiting admin", todo: "Not started" };
const STATUS_PILL = { met: "emerald", rejected: "rose", pending: "amber", todo: "slate" };

function sourceLabel(v) {
  if (!v.verified) return null;
  return { automatic: "Verified · automatic", admin_override: "Verified · admin override", legacy: "Verified · legacy" }[v.verification_source] || "Verified";
}

const STORAGE_KEY = "advoset.admin.verificationMatrix.open";

export function VerificationMatrix({ v }) {
  const missing = v.requirements.filter((r) => !r.met);
  const listed = v.listing?.listing_approved;
  const [open, setOpen] = useState(() => window.localStorage.getItem(STORAGE_KEY) !== "0");
  const toggle = () => {
    const next = !open;
    setOpen(next);
    window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  };
  return (
    <div data-testid="verification-matrix" data-state={open ? "open" : "closed"}>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="verification-state">
        {v.verified
          ? <Pill color="emerald"><ShieldCheck size={10} /> {sourceLabel(v)}</Pill>
          : <Pill color="amber"><ShieldAlert size={10} /> Not verified</Pill>}
        <span data-testid="verification-listing-pill">
          <Pill color={listed ? "blue" : "slate"}><Globe size={10} /> {listed ? "Listed" : "Unlisted"}</Pill>
        </span>
        <Pill color={v.available ? "emerald" : "slate"}>{v.available ? "Online" : "Offline"}</Pill>
        {v.auto_verification_suspended && (
          <span data-testid="verification-suspended"><Pill color="rose">Auto-verification suspended</Pill></span>
        )}
        {v.account_status !== "active" && <Pill color="rose">{v.account_status}</Pill>}
      </div>

      <div className="mb-3 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${v.all_met ? "bg-emerald-500" : "bg-gradient-to-r from-[#0A2540] to-[#D4AF37]"}`}
               style={{ width: `${v.percent}%` }} />
        </div>
        <span className="font-bold text-[#0A2540] text-sm" data-testid="verification-percent">
          {v.percent}% · {v.met}/{v.total}
        </span>
      </div>

      <p className="text-xs text-slate-500 mb-3" data-testid="verification-summary">
        {v.all_met
          ? "All 10 canonical requirements are met."
          : `${missing.length} requirement${missing.length === 1 ? "" : "s"} outstanding: ${missing.map((r) => r.label).join("; ")}.`}
        {v.verified && !listed && " Verified but not yet listed — approve listing from the Lawyers table."}
        {!v.verified && v.all_met && v.auto_verification_suspended && " Automatic verification is suspended; use Resume to re-run it."}
      </p>

      <button type="button" onClick={toggle} aria-expanded={open} aria-controls="verification-requirements"
        data-testid="verification-matrix-toggle"
        className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#0A2540] hover:underline underline-offset-4">
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        {open ? "Hide" : "Show"} {v.total} requirements
      </button>

      {open && (
      <ul id="verification-requirements" className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="verification-requirements">
        {v.requirements.map((r) => (
          <li key={r.key} className="flex items-start gap-2 text-sm py-1.5 px-2 rounded-lg bg-slate-50/60"
              data-testid={`requirement-${r.key}`}>
            {ICON[r.status] || ICON.todo}
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[#0A2540] font-medium">{r.label}</p>
                <span data-testid={`requirement-status-${r.key}`}>
                  <Pill color={STATUS_PILL[r.status] || "slate"}>{STATUS_LABEL[r.status] || r.status}</Pill>
                </span>
              </div>
              {r.detail && <p className="text-xs text-slate-500 mt-0.5">{r.detail}</p>}
            </div>
          </li>
        ))}
      </ul>
      )}

      {(v.verification_override || v.auto_verification_suspended) && (
        <div className="mt-3 text-xs text-slate-500 space-y-1" data-testid="verification-meta">
          {v.verification_override && (
            <p>Override by {v.verification_override.by?.email || v.verification_override.by?.user_id} on{" "}
              {v.verification_override.at?.slice(0, 10)} — “{v.verification_override.reason}” (readiness then {v.verification_override.readiness_percent}%).</p>
          )}
          {v.auto_verification_suspended && (
            <p>Suspended by {v.auto_verification_suspended_by?.email || "admin"} — “{v.auto_verification_suspended_reason}”.</p>
          )}
        </div>
      )}
    </div>
  );
}
